import React, { useState, useRef } from "react";

interface CodeBlockProps {
  language?: string;
  children: React.ReactNode;
}

export default function CodeBlock({ language, children }: CodeBlockProps) {
  const [copied, setCopied] = useState(false);
  const codeRef = useRef<HTMLElement>(null);

  const handleCopy = () => {
    const text = codeRef.current?.textContent ?? "";
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div
      className="rounded-lg my-3 overflow-hidden group/code"
      style={{ border: "1px solid var(--c-border)" }}
    >
      {/* Header bar */}
      <div
        className="flex items-center justify-between px-3 py-1.5"
        style={{ backgroundColor: "var(--c-surface-2)" }}
      >
        <span
          className="text-[10px] uppercase tracking-wide font-mono"
          style={{ color: "var(--c-text-faint)" }}
        >
          {language || "code"}
        </span>
        <button
          onClick={handleCopy}
          className="flex items-center gap-1 px-2 py-0.5 rounded text-[10px] transition-all opacity-0 group-hover/code:opacity-100"
          style={{
            color: copied ? "var(--c-success)" : "var(--c-text-faint)",
            backgroundColor: copied ? "rgba(18, 165, 148, 0.1)" : undefined,
          }}
          onMouseEnter={(e) => {
            if (!copied) e.currentTarget.style.backgroundColor = "var(--c-hover)";
          }}
          onMouseLeave={(e) => {
            if (!copied) e.currentTarget.style.backgroundColor = "";
          }}
        >
          {copied ? (
            <>
              <svg
                width="12"
                height="12"
                viewBox="0 0 12 12"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
              >
                <path d="M2 6.5l2.5 2.5L10 3" />
              </svg>
              Copied!
            </>
          ) : (
            <>
              <svg
                width="12"
                height="12"
                viewBox="0 0 12 12"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
              >
                <rect x="4" y="4" width="6" height="6" rx="1" />
                <path d="M8 4V2.5A.5.5 0 007.5 2H2.5a.5.5 0 00-.5.5v5a.5.5 0 00.5.5H4" />
              </svg>
              Copy
            </>
          )}
        </button>
      </div>

      {/* Code content */}
      <pre
        className="px-3 py-3 overflow-x-auto text-[13px] leading-relaxed font-mono"
        style={{ backgroundColor: "var(--c-bg)", margin: 0 }}
      >
        <code ref={codeRef} className={language ? `hljs language-${language}` : "hljs"}>
          {children}
        </code>
      </pre>
    </div>
  );
}
